import axios from 'axios';
import { Pergunta, Pesquisa, Usuario, Resposta } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mapeamento-saude-api-vptgd6j33q-uc.a.run.app/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);

export const pesquisaService = {
  getById: async (id: string): Promise<Pesquisa> => {
    const response = await api.get(`/pesquisas/${id}`);
    return response.data.data;
  },

  getActive: async (): Promise<Pesquisa[]> => {
    const response = await api.get('/pesquisas?ativo=true');
    return response.data.pesquisas;
  },
};

export const perguntaService = {
  getByPesquisa: async (pesquisaId: string): Promise<Pergunta[]> => {
    const response = await api.get(`/perguntas?pesquisa_id=${pesquisaId}&ativo=true&limit=100`);
    return response.data.data;
  },

  getById: async (id: string): Promise<Pergunta> => {
    const response = await api.get(`/perguntas/${id}`);
    return response.data.pergunta;
  },
};

export const usuarioService = {
  create: async (usuario: Partial<Usuario>): Promise<Usuario> => {
    
    try {
      const response = await api.post('/users', usuario);
      return response.data.data;
    } catch (error) {
      console.error('Erro na API de criação de usuário:', error);
      throw error;
    }
  },

  getById: async (id: string): Promise<Usuario> => {
    const response = await api.get(`/users/${id}`);
    return response.data.data;
  },

  getByCpf: async (cpf: string): Promise<Usuario | null> => {
    try {
      const response = await api.get(`/users?cpf=${cpf}`);
      return response.data.data || null;
    } catch (error) {
      return null;
    }
  },

  getByCpfAndEmail: async (cpf: string, email: string): Promise<Usuario | null> => {
    try {
      const response = await api.get(`/users?cpf=${cpf}&email=${email}`);
      return response.data.data || null;
    } catch (error) {
      return null;
    }
  },

  getByCpfAndPesquisa: async (cpf: string, pesquisaId: string): Promise<Usuario | null> => {
    try {
      const response = await api.get(`/users?cpf=${cpf}&pesquisa_id=${pesquisaId}`);
      return response.data.data || null;
    } catch (error) {
      return null;
    }
  },

  update: async (id: string, usuario: Partial<Usuario>): Promise<Usuario> => {
    const response = await api.put(`/users/${id}`, usuario);
    return response.data.data;
  },
};

export const respostaService = {
  create: async (resposta: Partial<Resposta>): Promise<Resposta> => {
    const response = await api.post('/respostas', resposta);
    return response.data.resposta;
  },

  createBatch: async (data: { pesquisa_id: string; usuario_id: string; respostas: Partial<Resposta>[] }): Promise<Resposta[]> => {
    const response = await api.post('/respostas/lote', data);
    return response.data.novasRespostas;
  },

  getByUsuarioAndPesquisa: async (usuarioId: string, pesquisaId: string): Promise<Resposta[]> => {
    const response = await api.get(`/respostas?usuario_id=${usuarioId}&pesquisa_id=${pesquisaId}`);
    return response.data.respostas;
  },
};

export const sessaoService = {
  getByUsuarioAndPesquisa: async (usuarioId: string, pesquisaId: string): Promise<any> => {
    const response = await api.get(`/sessoes?usuario_id=${usuarioId}&pesquisa_id=${pesquisaId}`);
    return response.data.data;
  },

  getByCpfAndPesquisa: async (cpf: string, pesquisaId: string): Promise<any> => {
    try {
      const response = await api.get(`/sessoes?cpf=${cpf}&pesquisa_id=${pesquisaId}`);
      return response.data.data;
    } catch (error) {
      return null;
    }
  },

  createOrUpdate: async (usuarioId: string, pesquisaId: string, dados: any): Promise<any> => {
    const response = await api.put(`/sessoes/usuario/${usuarioId}/pesquisa/${pesquisaId}`, dados);
    return response.data.data;
  },

  updateProgress: async (usuarioId: string, pesquisaId: string, progresso: any): Promise<any> => {
    const response = await api.put(`/sessoes/usuario/${usuarioId}/pesquisa/${pesquisaId}`, progresso);
    return response.data.data;
  },

  create: async (dados: any): Promise<any> => {
    
    try {
      const response = await api.post('/sessoes', dados);
      return response.data.data;
    } catch (error) {
      console.error('Erro na API de criação de sessão:', error);
      throw error;
    }
  },

  update: async (sessaoId: string, dados: any): Promise<any> => {
    const response = await api.put(`/sessoes/${sessaoId}`, dados);
    return response.data.data;
  },

  getById: async (sessaoId: string): Promise<any> => {
    const response = await api.get(`/sessoes/${sessaoId}`);
    return response.data.data;
  },
};

export const feedbackService = {
  getFeedbacks: async (perguntaId?: string, categoria?: string): Promise<any[]> => {
    const params = new URLSearchParams();
    if (perguntaId) params.append('pergunta_id', perguntaId);
    if (categoria) params.append('categoria', categoria);
    
    const response = await api.get(`/feedbacks?${params.toString()}`);
    return response.data.data;
  },

  createFeedback: async (dados: any): Promise<any> => {
    const response = await api.post('/feedbacks', dados);
    return response.data.data;
  },

  generateFeedbacks: async (usuarioId: string, sessaoId: string): Promise<any[]> => {
    const response = await api.post(`/feedbacks/gerar/${usuarioId}/${sessaoId}`);
    return response.data.data;
  },

  getUserFeedbacks: async (usuarioId: string, sessaoId: string): Promise<any[]> => {
    const response = await api.get(`/feedbacks/usuario/${usuarioId}/${sessaoId}`);
    return response.data.data;
  },

  markAsSent: async (feedbackId: number): Promise<any> => {
    const response = await api.patch(`/feedbacks/usuario/${feedbackId}/enviar-email`);
    return response.data.data;
  },

  sendSessionEmail: async (sessaoId: string, email: string): Promise<any> => {
    const response = await api.post(`/feedbacks/sessao/${sessaoId}/email`, { email });
    return response.data.data;
  },

  sendUserEmail: async (usuarioId: string, sessaoId: string, email: string): Promise<any> => {
    const response = await api.post(`/feedbacks/usuario/${usuarioId}/email`, { 
      sessao_id: sessaoId, 
      email 
    });
    return response.data.data;
  },

  sendSessionEmailsGrouped: async (usuarioIds: string[], emailTitular: string): Promise<{ email: string; total_emails: number; total_feedbacks: number }> => {
    const response = await api.post('/feedbacks/email-agrupado', { usuario_ids: usuarioIds, email: emailTitular });
    return response.data.data;
  },
};

export interface ImcClassificacaoData {
  imc: number;
  classificacao: string | null;
  gestante: boolean;
  semana_gestacional?: number;
  erro?: string;
}

export const imcService = {
  getClassificacao: async (usuarioId: string): Promise<ImcClassificacaoData | null> => {
    const response = await api.get(`/imc/classificacao/${usuarioId}`);
    return response.data.data;
  },
};

export default api;
